import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Copy, Sparkles, Check, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user, loading, refresh } = useAuth();
  const [form, setForm] = useState({ bio: "", location: "", interests: "" });
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState({ invites: [], remaining: 3, max: 3 });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        bio: user.bio || "",
        location: user.location || "",
        interests: (user.interests || []).join(", "),
      });
      loadInvites();
    }
  }, [user]);

  const loadInvites = async () => {
    try {
      const r = await axios.get(`${API}/invitations/me`);
      setInvites(r.data);
    } catch (e) { console.error("Failed to load invites:", e); }
  };

  if (loading) return <div className="py-32 text-center text-tc-muted italic font-serif text-xl">Un istante…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put(`${API}/users/me`, {
        bio: form.bio,
        location: form.location,
        interests: form.interests.split(",").map(s => s.trim()).filter(Boolean),
      });
      await refresh();
      toast.success("Profilo aggiornato");
    } catch { toast.error("Qualcosa non è andato"); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    window.location.href = "/";
  };

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      await axios.post(`${API}/invitations`);
      await loadInvites();
      toast.success("Invito creato. Condividi il link con chi senti.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setCreatingInvite(false); }
  };

  const inviteUrl = (code) => `${window.location.origin}/login?invite=${code}`;

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(code));
      setCopied(code);
      toast.success("Link copiato");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Copia manuale");
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 md:px-12 py-16" data-testid="profile-page">
      <div className="flex items-center gap-6 mb-10">
        {user.picture && <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full border-2 border-tc-accent/40" />}
        <div>
          <div className="text-xs uppercase tracking-widest text-tc-secondary flex items-center gap-2">
            Ben tornata, ben tornato
            {user.is_admin && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tc-secondary/10 text-tc-secondary text-[10px]"><ShieldCheck className="w-3 h-3" /> Admin</span>}
          </div>
          <h1 className="font-serif text-4xl text-tc-primary">{user.name}</h1>
          <div className="text-sm text-tc-muted">{user.email}</div>
        </div>
      </div>

      {/* INVITES SECTION */}
      <section className="bg-tc-surface rounded-3xl p-8 border border-tc/50 mb-8" data-testid="invites-section">
        <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
          <div>
            <h2 className="font-serif text-3xl text-tc-primary flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-tc-secondary" /> I tuoi 3 inviti
            </h2>
            <p className="text-sm text-tc-muted mt-2 max-w-lg">
              Cresciamo per fiducia, non per virailità. Hai <strong>{invites.remaining}</strong> {invites.remaining === 1 ? 'invito' : 'inviti'} da regalare a chi senti.
            </p>
          </div>
          <button onClick={createInvite} disabled={creatingInvite || invites.remaining === 0}
            className="btn-primary disabled:opacity-40 text-sm !py-2 !px-5" data-testid="create-invite-btn">
            {creatingInvite ? "Creazione…" : invites.remaining === 0 ? "Inviti esauriti" : "Genera invito"}
          </button>
        </div>

        <div className="space-y-3 mt-6">
          {invites.invites.length === 0 ? (
            <p className="italic text-tc-muted text-sm">Nessun invito ancora generato.</p>
          ) : (
            invites.invites.map(inv => (
              <div key={inv.code} className="flex items-center justify-between bg-tc-bg rounded-2xl p-4 border border-tc/40 flex-wrap gap-3" data-testid={`invite-${inv.code}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-widest text-tc-accent">
                    {inv.used ? "Usato" : "Disponibile"}
                  </div>
                  {inv.used && inv.redeemed_by ? (
                    <div className="text-sm text-tc-primary mt-1">
                      Accolto da <strong>{inv.redeemed_by.name}</strong>
                    </div>
                  ) : (
                    <div className="text-sm text-tc-muted mt-1 truncate font-mono">{inviteUrl(inv.code)}</div>
                  )}
                </div>
                {!inv.used && (
                  <button onClick={() => copyLink(inv.code)} className="btn-outline !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid={`copy-${inv.code}`}>
                    {copied === inv.code ? <><Check className="w-4 h-4" /> Copiato</> : <><Copy className="w-4 h-4" /> Copia link</>}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {/* PROFILE FORM */}
      <form onSubmit={save} className="space-y-6 bg-tc-surface rounded-3xl p-8 border border-tc/50">
        <h2 className="font-serif text-3xl text-tc-primary">Il tuo profilo</h2>
        <div>
          <label className="text-xs uppercase tracking-widest text-tc-muted">Dove abiti</label>
          <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
            placeholder="Es. Livorno, Toscana"
            className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="profile-location" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-tc-muted">Racconta di te</label>
          <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} rows={4}
            placeholder="Una frase, una pratica, una direzione."
            className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary resize-none" data-testid="profile-bio" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-tc-muted">Interessi (separati da virgola)</label>
          <input value={form.interests} onChange={e => setForm({ ...form, interests: e.target.value })}
            placeholder="permacultura, meditazione, autoproduzione"
            className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="profile-interests" />
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="profile-save">{busy ? "Salvo…" : "Salva"}</button>
          <button type="button" onClick={logout} className="btn-outline" data-testid="profile-logout">Esci</button>
        </div>
      </form>
    </main>
  );
}
